import React, { useMemo } from 'react';

import { ISensorFusionSubstageDefinitionLookupDto } from '@scale/sensor-fusion-shared';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';

import { CORP_LOOKUP_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import { LookupIdLink } from 'react-scale/dashboard.scale.com/components/SensorFusionLookup/common';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

type SubstageDefinitionOverviewPanelProps = {
  substageDefinition: ISensorFusionSubstageDefinitionLookupDto;
};

const SubstageDefinitionOverviewPanel = ({
  substageDefinition,
}: SubstageDefinitionOverviewPanelProps) => {
  const { customer, createdAt, project, childProject, stageDefinition, taxonomy, childTaskType } =
    substageDefinition;
  const { permissionGroup } = childProject;

  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};
  const { isRemoAdmin } = useRemoAdminCtx();

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  return (
    <div className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2">
      {!isRemoAdmin && (
        <div className="flex flex-col">
          <div>
            <span className="font-medium">Customer: </span>
            {customer.company != null && `${customer.company} `}
            <WithCopy className="inline-block text-info-700" copyText={customer.id}>
              <a href={`${toolUrl}/${customer.id}`}>{customer.id}</a>
            </WithCopy>
          </div>
          <div className="flex flex-col ml-2">
            {customer.email != null && (
              <WithCopy copyText={customer.email}>Email: {customer.email}</WithCopy>
            )}
          </div>
        </div>
      )}

      <div>
        <span className="font-medium">Unified Project: </span>
        {`${project.name} `}
        <WithCopy className="inline-block text-info-700" copyText={project.id}>
          <a href={`${toolUrl}/${project.id}`}>{project.id}</a>
        </WithCopy>
      </div>

      <div>
        <span className="font-medium">Permission Group: </span>
        {`${permissionGroup.name} `}
        <WithCopy className="inline-block text-info-700" copyText={permissionGroup.id}>
          <a href={`${toolUrl}/${permissionGroup.id}`}>{permissionGroup.id}</a>
        </WithCopy>
      </div>

      <div className="flex flex-col gap-1">
        <span className="font-medium">Stage Definition: </span>
        {`${stageDefinition.name} `}
        <WithCopy className="inline-block text-info-700" copyText={stageDefinition.id}>
          <a href={`${toolUrl}/${stageDefinition.id}`}>{stageDefinition.id}</a>
        </WithCopy>
      </div>

      {taxonomy && !isRemoAdmin && (
        <div>
          <span className="font-medium">Taxonomy: </span>
          <LookupIdLink className="inline-block" id={taxonomy.srn ?? taxonomy.id} />
        </div>
      )}

      <div>
        <span className="font-medium">Type: </span>
        {childTaskType}
      </div>

      <div>
        <span className="font-medium">Created At: </span>
        {new Date(createdAt).toLocaleString()}
      </div>
    </div>
  );
};

export default SubstageDefinitionOverviewPanel;
